// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how the Assistant is used (the server's engine/UsageLog). The app
// reports what only it sees (which suggestion button was pressed, what was
// changed in the create-task form); Settings shows the numbers back.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';

export interface KonvoCardUsage {
	prepared: number;
	confirmed: number;
	edited: number;
	cancelled: number;
	ignored: number;
	failed: number;
	changed_fields: Array<{field: string; count: number}>;
}

export interface KonvoAssistantUsage {
	cards: Record<string, KonvoCardUsage>;
	turns: {
		count: number;
		p50_ms: number | null;
		p90_ms: number | null;
		stopped: number;
		errors: number;
		by_provider: Record<string, number>;
		top_tools: Array<{tool: string; count: number}>;
	};
	strip: Record<string, {create: number; ask_assistant: number; dismiss: number}>;
	task_form: {
		from_draft: number;
		created: number;
		untouched: number;
		changed_fields: Array<{field: string; count: number}>;
	};
}

export interface KonvoTeamUsage {
	days: number;
	total: KonvoAssistantUsage;
	people: Array<{user_id: string; name: string; events: number; stats: KonvoAssistantUsage}>;
}

type ClientUsage =
	| {
			kind: 'strip_action';
			subject: 'event' | 'reminder' | 'task' | 'block_time' | 'meeting';
			data: {action: 'create' | 'ask_assistant' | 'dismiss'; item_id?: string};
	  }
	| {
			kind: 'task_form';
			subject: 'strip' | 'menu' | 'mail';
			data: {from_draft: boolean; created: boolean; changed: Array<string>};
			content?: {draft: unknown; final: unknown};
	  }
	| {
			/** An action on the email summary card: `open` when tapped, `done` when the reply was sent or the event or task created. */
			kind: 'mail_summary';
			subject: 'reply' | 'event' | 'task' | 'meeting';
			data: {action: 'open' | 'done'};
	  }
	| {
			/** The summary card hidden with its close button: an explicit "not wanted". */
			kind: 'mail_summary';
			subject: 'card';
			data: {action: 'dismiss'};
	  };

/** Best-effort: a lost report never gets in the user's way. */
export function reportAssistantUsage(event: ClientUsage): void {
	void http.post(Endpoints.KONVO_ASSISTANT_USAGE, {body: event}).catch(() => undefined);
}

export async function fetchUsageExport(): Promise<string> {
	const response = await http.get<unknown>(Endpoints.KONVO_ASSISTANT_USAGE_EXPORT);
	return JSON.stringify(response.body, null, 2);
}

/** Null when the caller is not staff (the server refuses) or it cannot be reached. */
export async function fetchTeamUsage(days = 14): Promise<KonvoTeamUsage | null> {
	try {
		const response = await http.get<KonvoTeamUsage>(`${Endpoints.KONVO_ASSISTANT_USAGE_TEAM}?days=${days}`);
		return response.body ?? null;
	} catch {
		return null;
	}
}

/** Which of the drafted task fields the user changed before creating (names only). */
export function changedTaskFields(
	draft: Record<string, unknown> | null | undefined,
	final: Record<string, unknown>,
): Array<string> {
	if (!draft) return [];
	return Object.keys(final)
		.filter((key) => JSON.stringify(draft[key] ?? null) !== JSON.stringify(final[key] ?? null))
		.sort();
}

export interface CardUsageLine {
	kind: string;
	confirmed: number;
	edited: number;
	cancelled: number;
	ignored: number;
	/** The fields changed most often, at most three. */
	fields: Array<string>;
}

/** Pure: card kinds that saw any use, busiest first. */
export function cardUsageLines(usage: KonvoAssistantUsage | null | undefined): Array<CardUsageLine> {
	return Object.entries(usage?.cards ?? {})
		.filter(([, card]) => card.prepared + card.confirmed + card.cancelled > 0)
		.sort(([, a], [, b]) => b.prepared - a.prepared)
		.map(([kind, card]) => ({
			kind,
			confirmed: card.confirmed,
			edited: card.edited,
			cancelled: card.cancelled,
			ignored: card.ignored,
			fields: card.changed_fields.slice(0, 3).map((entry) => entry.field),
		}));
}

/** Pure: a median reply time for people, "4 s" or "1.2 min". */
export function formatReplyTime(ms: number | null): string | null {
	if (ms == null || !Number.isFinite(ms)) return null;
	return ms < 60_000 ? `${Math.max(1, Math.round(ms / 1000))} s` : `${(ms / 60_000).toFixed(1)} min`;
}

/** `konvo-usage-2026-09-23.json`, in the user's local date. */
export function usageExportFileName(now: Date): string {
	const pad = (value: number) => String(value).padStart(2, '0');
	return `konvo-usage-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.json`;
}
